import { Delete } from '@mui/icons-material';
import { Button } from '@mui/material';
import type { DraftPlayerResponse } from '@on3/api';
import { type ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { externalApi, useAuth, useToast } from '@on3/ui-lib/index';
import { type AxiosError } from 'axios';
import clsx from 'clsx';
import { On3Avatar } from 'components/Image/On3Avatar';
import { TeamAvatar } from 'components/Image/TeamAvatar';
import { getAbbreviatedRank } from 'components/Nil/helpers';
import { TextLink } from 'components/UI/Link/TextLink';
import { StarRating } from 'components/UI/StarRating/StarRating';
import { Text } from 'components/UI/Typography/Text';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';

import styles from './DraftItem.module.scss';

export const DraftItem = ({
  draftPick,
}: {
  draftPick: DraftPlayerResponse;
}) => {
  const {
    round,
    pick,
    organization,
    collegeOrganization,
    rating,
    player,
    year: draftYear,
    rosterRating,
    overallPick,
    key,
  } = draftPick || {};
  const {
    highSchool,
    fullName,
    defaultAsset,
    slug,
    age,
    classRank,
    height,
    weight,
    position,
    state,
    hometown,
  } = player || {};
  const router = useRouter();
  const isNBA = router.pathname.includes('nba');
  const isTeamDraft = router.pathname.includes('draft-history/[category]');
  const sport = isNBA ? 'basketball' : 'football';
  const { isDeveloper, hasRdbLevel4Access } = useAuth();
  const canDelete = (isDeveloper() || hasRdbLevel4Access()) && !isTeamDraft;
  const { error, success } = useToast();
  const [loading, setLoading] = useState(false);
  const [deleted, setDeleted] = useState(false);

  const ratingTeamLogo = {
    src:
      collegeOrganization?.assetUrl ||
      'https://on3static.com/teams/default.svg',
    altText: collegeOrganization?.name || 'rating logo',
  };

  const handleDeletePick = useCallback(async () => {
    const isConfirmed = window.confirm(
      'Are you sure you want to delete this pick?',
    );

    if (!isConfirmed) return;
    try {
      setLoading(true);

      externalApi
        .delete(`/rdb/v1/draft-picks/${key}`)
        .then((r) => {
          setDeleted(true);
          success(`Pick Deleted`);
        })
        .catch(() => {
          success(`something went wrong. Try again`);
        });
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      error(response?.data?.message || message);
    } finally {
      setLoading(false);
    }
  }, [error, key, success]);

  if (deleted) {
    return null;
  }

  return (
    <li className={styles.listItem}>
      <div
        className={clsx(
          styles.draftItem,
          { [styles.teamDraft]: isTeamDraft },
          { [styles.deleteWrapper]: canDelete },
        )}
      >
        {isTeamDraft && <Text className={styles.draftYear}>{draftYear}</Text>}
        <div className={styles.pickBlock}>
          <div
            className={clsx(styles.pickContainer, {
              [styles.teamDraft]: isTeamDraft,
            })}
          >
            <Text className={styles.pick}>{overallPick}</Text>
            <Text className={styles.round} component="span" variant="subtitle1">
              <span aria-label="Round">RND</span>
              {round}
            </Text>
            <Text
              className={styles.overallPick}
              component="span"
              variant="subtitle1"
            >
              ({pick})
            </Text>
          </div>
          <Text className={styles.pickTeam}>
            <TeamAvatar
              className={styles.pickTeamLogo}
              height={20}
              slug={organization?.slug}
              src={organization?.assetUrl}
              width={20}
            />

            <Text className={styles.proName} component="span">
              {organization?.abbreviation}
            </Text>
          </Text>
        </div>
        <div className={styles.playerBlock}>
          <On3Avatar
            altText={`${fullName} Avatar`}
            className={styles.avatar}
            domain={defaultAsset?.domain}
            radiusFive
            source={defaultAsset?.source}
          />
          <TextLink
            className={styles.playerName}
            href={`/rivals/${slug}/`}
            underline="none"
            variant="h5"
          >
            {fullName}
          </TextLink>

          <Text className={styles.playerDetails}>
            <span className={styles.ageContainer}>
              <span className={styles.ageLabel}>Age</span>
              <span>{age || '-'}</span>
            </span>
            <span className={styles.addSlash}>
              {getAbbreviatedRank(classRank || '')}
            </span>
            <span className={styles.addSlash}>{height ?? '-'}</span>
            <span>{weight ?? '-'}</span>
          </Text>
          <Text className={styles.mobilePosition}>{position?.abbr ?? '-'}</Text>
        </div>
        <Text className={styles.desktopPosition}>{position?.abbr}</Text>

        <div className={styles.starsWrapper}>
          <div className={styles.starsContainer}>
            <div className={styles.collegeRatingContainer}>
              <StarRating
                fiveStarPlus={rosterRating?.fiveStarPlus}
                hasBorder={false}
                href={`/rivals/${slug}/industry-comparison/`}
                isBoldRating={true}
                isConsensus={false}
                rating={rosterRating?.rating}
                rounding={2}
                sport={sport}
                stars={rosterRating?.stars}
                teamLogo={ratingTeamLogo}
                year={`${rosterRating?.year}`}
              />
            </div>
            <StarRating
              fiveStarPlus={rating?.fiveStarPlus}
              href={`/rivals/${slug}/industry-comparison/`}
              isBoldRating={false}
              isConsensus={true}
              rating={rating?.consensusRating}
              rounding={2}
              stars={rating?.consensusStars}
            />
          </div>
        </div>
        <div className={styles.homeWrapper}>
          <Text className={styles.homeContainer} variant="caption">
            <span className={styles.state}>{state?.abbr}</span>
            <TextLink
              className={styles.highSchool}
              href={`/high-school/${highSchool?.urlSlug}/`}
            >
              {highSchool?.name}
            </TextLink>
            <span className={styles.hometown}>{hometown?.name}</span>
          </Text>
        </div>
        {canDelete && (
          <Button
            aria-label="refresh-game"
            className={clsx(styles.deleteButton, {
              [styles.spinButton]: loading,
            })}
            color="primary"
            disableRipple
            disabled={loading}
            onClick={handleDeletePick}
            startIcon={<Delete />}
          />
        )}
      </div>
    </li>
  );
};
